<script lang="ts">
  import { markdownPrompt, type MarkdownDocxStats, type Profile } from '@gostword/engine';
  import { untrack } from 'svelte';
  import EXAMPLE from '../../../engine/test/fixtures/lab.md?raw';
  import { deliverFile } from '../lib/download';
  import type { MarkdownRequest, MarkdownResponse } from '../lib/engine';
  import { countOf } from '../lib/format';
  import { loadTitlePage, saveTitlePage } from '../lib/titlepage';
  import Icon from './Icon.svelte';

  let {
    profile,
    initialText = '',
    onBuild,
    onCheck,
    onProfile,
    onClose,
  }: {
    profile: Profile;
    initialText?: string;
    onBuild: (r: MarkdownRequest) => Promise<MarkdownResponse>;
    onCheck: (file: ArrayBuffer, name: string) => void;
    onProfile: () => void;
    onClose: () => void;
  } = $props();

  const PLACEHOLDER = '## Введение\n\nТекст работы со ссылкой на источник [1].\n\n## 1. Теоретическая часть\n\n$$E = mc^2$$\n\n| Параметр | Значение |\n|---|---|\n| R | 10 кОм |';

  let dialog: HTMLDialogElement;
  let fileInput = $state<HTMLInputElement>();
  let text = $state(untrack(() => initialText));
  let toc = $state(true);
  let numbers = $state(true);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let done = $state.raw<(MarkdownResponse & { name: string; delivered: 'downloaded' | 'sent' }) | null>(null);
  let promptCopied = $state(false);
  const prompt = $derived(markdownPrompt(profile));
  const title = $state(loadTitlePage());
  const year = String(new Date().getFullYear());

  $effect(() => saveTitlePage($state.snapshot(title)));

  async function copyPrompt(): Promise<void> {
    try {
      await navigator.clipboard.writeText(prompt);
      promptCopied = true;
      setTimeout(() => (promptCopied = false), 2500);
    } catch {
      window.prompt('Скопируйте промпт:', prompt);
    }
  }

  $effect(() => {
    dialog.showModal();
  });

  const fileName = (title?: string): string => {
    const base = (title ?? '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
    return `${base || 'Документ'} (ГОСТ).docx`;
  };

  function summary(s: MarkdownDocxStats): string {
    return [
      s.headings ? countOf(s.headings, ['заголовок', 'заголовка', 'заголовков']) : '',
      s.tables ? countOf(s.tables, ['таблица', 'таблицы', 'таблиц']) : '',
      s.figures ? countOf(s.figures, ['рисунок', 'рисунка', 'рисунков']) : '',
      s.formulas ? countOf(s.formulas, ['формула', 'формулы', 'формул']) : '',
      s.listings ? countOf(s.listings, ['листинг кода', 'листинга кода', 'листингов кода']) : '',
      s.sources ? countOf(s.sources, ['источник', 'источника', 'источников']) : '',
    ]
      .filter(Boolean)
      .join(' · ');
  }

  async function submit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (!text.trim()) {
      error = 'Вставьте текст — например, ответ нейросети целиком.';
      return;
    }
    busy = true;
    error = null;
    try {
      const res = await onBuild({
        text,
        toc: toc ? undefined : false,
        numberSections: numbers,
        titlePage: title.enabled ? $state.snapshot(title.info) : false,
      });
      const name = fileName(res.title);
      const delivered = await deliverFile(res.file, name, `Документ собран: ${summary(res.stats)}. Требования: ${profile.name}.`);
      done = { ...res, name, delivered };
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      busy = false;
    }
  }
</script>

<dialog
  class="modal wide"
  bind:this={dialog}
  onclose={onClose}
  onclick={(e) => {
    if (e.target === dialog) dialog.close();
  }}
>
  {#if done}
    <div class="done">
      <div class="done-icon"><Icon name="check" size={28} /></div>
      <h2>{done.delivered === 'sent' ? 'Документ собран и отправлен в чат' : 'Документ собран'}</h2>
      <p class="name">{done.name}</p>
      <p>{summary(done.stats)}</p>
      {#if done.warnings.length}
        <ul class="warnings">
          {#each done.warnings as w (w)}<li>{w}</li>{/each}
        </ul>
      {/if}
      <div class="done-actions">
        <button class="btn" onclick={() => dialog.close()}>Готово</button>
        <button
          class="btn btn-primary"
          onclick={() => {
            onCheck(done!.file, done!.name);
            dialog.close();
          }}
        >
          Проверить документ
        </button>
      </div>
    </div>
  {:else}
    <form onsubmit={submit}>
      <header class="dlg-head">
        <div>
          <h2>Документ из текста нейросети</h2>
          <p>
            Вставьте ответ ChatGPT, DeepSeek, GigaChat или любой текст в Markdown: заголовки, списки, таблицы, формулы LaTeX и код
            станут документом по {profile.name}. Всё собирается на вашем устройстве.
          </p>
        </div>
        <button type="button" class="icon-btn" aria-label="Закрыть" onclick={() => dialog.close()}><Icon name="x" /></button>
      </header>

      <div class="dlg-body">
        <textarea class="source" bind:value={text} rows="14" spellcheck="false" placeholder={PLACEHOLDER} aria-label="Текст в Markdown"></textarea>
        <div class="tools">
          <button type="button" class="link" onclick={() => fileInput?.click()}>Открыть файл .md</button>
          <span aria-hidden="true">·</span>
          <button type="button" class="link" onclick={() => (text = EXAMPLE)}>Вставить пример</button>
          <input
            bind:this={fileInput}
            class="visually-hidden"
            type="file"
            tabindex="-1"
            accept=".md,.markdown,.txt,text/markdown,text/plain"
            onchange={async (e) => {
              const file = e.currentTarget.files?.[0];
              e.currentTarget.value = '';
              if (file) text = await file.text();
            }}
          />
        </div>
        <details class="prompt">
          <summary>Что написать нейросети, чтобы текст собрался без правок</summary>
          <p>Добавьте это к своему запросу — ответ придёт в Markdown, который здесь соберётся в документ по {profile.name}.</p>
          <pre>{prompt}</pre>
          <button type="button" class="btn" onclick={copyPrompt}>
            <Icon name={promptCopied ? 'check' : 'edit'} size={16} />
            {promptCopied ? 'Скопировано' : 'Скопировать промпт'}
          </button>
        </details>
        <details class="panel">
          <summary>Титульный лист</summary>
          <label class="check">
            <input type="checkbox" bind:checked={title.enabled} />
            <span>Первая страница — титульный лист: номер на ней не ставится, внизу — город и год</span>
          </label>
          {#if title.enabled}
            <div class="fields">
              <label class="field full">
                <span>Организация</span>
                <textarea rows="3" bind:value={title.info.organization} placeholder={'Министерство…\nУниверситет…\nКафедра…'}></textarea>
              </label>
              <label class="field"><span>Вид работы</span><input bind:value={title.info.workType} placeholder="ЛАБОРАТОРНАЯ РАБОТА № 1" /></label>
              <label class="field"><span>Дисциплина</span><input bind:value={title.info.discipline} placeholder="Базы данных" /></label>
              <label class="field">
                <span>Выполнил</span>
                <textarea rows="2" bind:value={title.info.author} placeholder={'студент группы ИС-21\nИ. И. Иванов'}></textarea>
              </label>
              <label class="field">
                <span>Проверил</span>
                <textarea rows="2" bind:value={title.info.supervisor} placeholder={'доцент кафедры\nП. П. Петров'}></textarea>
              </label>
              <label class="field"><span>Город</span><input bind:value={title.info.city} placeholder="Москва" /></label>
              <label class="field"><span>Год</span><input bind:value={title.info.year} inputmode="numeric" placeholder={year} /></label>
            </div>
            <p class="hint">
              Тема — из первой строки «# Название». Поля запоминаются только в этом браузере. Их можно задать и в начале текста блоком
              <code>---</code> со строками «вуз: …», «город: …».
            </p>
          {/if}
        </details>
        <label class="check"><input type="checkbox" bind:checked={toc} /><span>Содержание, если в работе больше трёх разделов</span></label>
        <label class="check"><input type="checkbox" bind:checked={numbers} /><span>Нумеровать разделы: 1, 1.1, 1.2</span></label>
        <p class="std">Требования: {profile.name}. <button type="button" class="link" onclick={onProfile}>Изменить</button></p>
        {#if error}<p class="error" role="alert">{error}</p>{/if}
      </div>

      <footer class="dlg-foot">
        <button type="button" class="btn" onclick={() => dialog.close()}>Отмена</button>
        <button type="submit" class="btn btn-primary" disabled={busy}>
          <Icon name="download" size={18} />
          {busy ? 'Собираем…' : 'Собрать .docx'}
        </button>
      </footer>
    </form>
  {/if}
</dialog>

<style>
  .wide {
    width: min(720px, calc(100vw - 32px));
  }

  .source {
    width: 100%;
    min-height: 260px;
    padding: 12px 14px;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--surface-2);
    color: var(--text);
    font:
      13.5px/1.5 ui-monospace,
      'Cascadia Mono',
      Consolas,
      'Liberation Mono',
      monospace;
    resize: vertical;
  }

  .source:focus,
  .field input:focus,
  .field textarea:focus {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  .panel {
    margin: 4px 0 10px;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: 12px;
    font-size: 14px;
  }

  .panel summary {
    font-weight: 560;
    cursor: pointer;
  }

  .panel .check {
    margin-top: 8px;
  }

  .fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 10px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    color: var(--text-2);
  }

  .field.full {
    grid-column: 1 / -1;
  }

  .field input,
  .field textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: var(--surface-2);
    color: var(--text);
    font: inherit;
    resize: vertical;
  }

  .hint {
    margin-top: 8px;
    color: var(--text-3);
  }

  @media (max-width: 560px) {
    .fields {
      grid-template-columns: 1fr;
    }
  }

  .tools {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 8px 0 6px;
    color: var(--text-3);
    font-size: 14px;
  }

  .prompt {
    margin: 4px 0 10px;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: 12px;
    font-size: 14px;
  }

  .prompt summary {
    font-weight: 560;
    cursor: pointer;
  }

  .prompt p {
    margin-top: 8px;
    color: var(--text-2);
  }

  .prompt pre {
    max-height: 220px;
    margin: 8px 0 10px;
    padding: 10px 12px;
    overflow: auto;
    border-radius: 10px;
    background: var(--surface-2);
    color: var(--text-2);
    font:
      12.5px/1.5 ui-monospace,
      'Cascadia Mono',
      Consolas,
      'Liberation Mono',
      monospace;
    white-space: pre-wrap;
  }

  .std {
    margin-top: 6px;
    color: var(--text-2);
    font-size: 14px;
  }

  .error {
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--error-soft);
    color: var(--error);
    font-size: 14px;
  }

  .done {
    padding: 32px 28px 26px;
    text-align: center;
  }

  .done-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 14px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: var(--ok);
    color: #fff;
  }

  .done h2 {
    font-size: 22px;
  }

  .done p {
    margin-top: 8px;
    color: var(--text-2);
  }

  .done .name {
    color: var(--text);
    font-weight: 560;
    overflow-wrap: anywhere;
  }

  .warnings {
    margin: 16px 0 0;
    padding: 12px 16px 12px 32px;
    border-radius: 12px;
    background: var(--surface-2);
    color: var(--text-2);
    font-size: 14px;
    text-align: left;
  }

  .warnings li + li {
    margin-top: 4px;
  }

  .done-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    margin-top: 22px;
  }
</style>
